import { OnOffIcon } from "@images/svgIcons/onOffIcon"
import { word } from "@modules/core/utils/i18n"
import { useProgramRights } from "@modules/programs/hooks/useProgramRights"
import { ProgramInfiniteTypeOf } from "@modules/programs/programInfiniteTypes"
import { ProgramOnOffTypeOf } from "@modules/programs/programOnOffTypes"
import { Program, ProgramVisibility } from "@modules/programs/programTypes"
import { getDisplayVisibility, getSubscriptionCountText } from "@modules/programs/programUtils"
import { HUText } from "@modules/ui/components/huText"
import { HURow } from "@modules/ui/components/layout"
import { GlobeIcon, InfinityIcon, LockIcon } from "lucide-react"
import { MenuItem } from "primereact/menuitem"
import { TabMenu } from "primereact/tabmenu"
import styled, { useTheme } from "styled-components"

export const ProgramInfoHeader: React.FC<{
  program: Program
  activeIndex?: number
  setActiveIndex?: (arg: number) => void
}> = ({ program, activeIndex, setActiveIndex }) => {
  const theme = useTheme()
  const { isAuthor, isAcceptedSharedCoach } = useProgramRights(program)

  const items: MenuItem[] = [
    { label: word("global.details") },
    {
      label:
        program._programType === ProgramOnOffTypeOf
          ? word("global.athlete", { count: 2 })
          : word("program.label.subscribers"),
    },
  ]

  return (
    <HeaderContainer>
      <div className="relative flex justify-content-center">
        <ImageContainer
          $bgImage={program.image.path}
          className="p-5 mb-5 flex-row justify-content-center align-items-center overflow-hidden gap-2 border-noround-top"
        >
          <GradientOverlay />
          <TitleContainer>
            <HURow className="align-items-center gap-2">
              {program._programType === ProgramInfiniteTypeOf ? (
                <InfinityIcon color={theme.mainWhite} width={24} height={24} />
              ) : (
                <OnOffIcon color={theme.mainWhite} width={24} height={24} />
              )}
              <HUText fontStyle="HS" color={theme.mainWhite}>
                {program.title}
              </HUText>
            </HURow>
            <div className="flex align-items-center">
              {program.visibility === ProgramVisibility.PUBLIC ? (
                <GlobeIcon color={theme.mainWhite} />
              ) : (
                <LockIcon color={theme.mainWhite} />
              )}
              <HUText color={theme.mainWhite} fontStyle="BM" className="ml-2">
                {`${word("program.details.visibility", {
                  visibility: getDisplayVisibility(program.visibility),
                })}
                  `}
              </HUText>
              <HUText fontStyle="HM" color={theme.mainWhite} className="ml-2">
                ·
              </HUText>
              <HUText fontStyle="BS" color={theme.mainWhite} className="ml-2">
                {getSubscriptionCountText(
                  program.subscriptionCount,
                  isAuthor,
                  isAcceptedSharedCoach,
                  true,
                  program._programType,
                ).toLowerCase()}
              </HUText>
            </div>
          </TitleContainer>
        </ImageContainer>
        {(isAuthor || isAcceptedSharedCoach) && !!setActiveIndex && (
          <MenuContainer model={items} activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} />
        )}
      </div>
    </HeaderContainer>
  )
}

const HeaderContainer = styled.div`
  margin: -85px -20px;
`

const ImageContainer = styled(HURow)<{ $bgImage?: string }>`
  position: relative;
  border-radius: 33px;
  border-width: 0px;
  height: 290px;
  width: 100%;
  background-image: ${({ $bgImage }) => `url(${$bgImage})`};
  background-size: cover;
  background-position: center;
`
const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(180deg, rgba(34, 34, 34, 0) 0%, rgba(34, 34, 34, 0.8) 99.9%);
  border-radius: 33px;
  border-width: 0px;
  height: 290px;
`
const TitleContainer = styled.div`
  position: absolute;
  bottom: 30px;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0px 30px;
  text-align: center;
`
const MenuContainer = styled(TabMenu)`
  background-color: ${({ theme }) => theme.neutralColor100};
  position: absolute;
  bottom: -12px;
  width: 95%;
  border: 2px solid ${({ theme }) => theme.neutralColor100};
  border-radius: 50px;

  .p-tabmenu-nav {
    background-color: ${({ theme }) => theme.neutralColor100};
    border-radius: 50px;
    display: flex;
    border: none;
  }

  .p-tabmenuitem {
    flex: 1;
    height: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: row;
  }

  .p-tabmenu-ink-bar  {
    display: none;
  }

  .p-tabmenu-nav .p-tabmenuitem .p-menuitem-link {
    background-color: ${({ theme }) => theme.neutralColor100};
    color: ${({ theme }) => theme.neutralColor900};
    border-radius: 50px;
    border: none;
    justify-content: center;
    font-weight: 400;
    height: 100%;
    padding: 0px;
    width: -webkit-fill-available;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }

  .p-tabmenu-nav .p-tabmenuitem.p-highlight .p-menuitem-link {
    border-radius: 50px;
    font-weight: 600;
    border: none;
    background-color: ${({ theme }) => theme.neutralColor900};
    color: ${({ theme }) => theme.mainWhite};
    width: 100%;
  }

  .p-tabmenu-nav .p-tabmenuitem:not(.p-highlight):not(.p-disabled):hover .p-menuitem-link {
    background-color: ${({ theme }) => theme.neutralColor100};
    color: ${({ theme }) => theme.neutralColor900};
    border-radius: 50px;
    border: none;
    justify-content: center;
  }
`
